// Pricing sheets awaiting review (Phase 4 slice 2b).
//
// §0 — the tab's one question: "which uploaded sheets still need a decision from
// me?" Primary object: a collection. Primary verb: open one. Worst case: a few
// dozen staged sheets per tenant.
//
// Archetype B (collection): title + count, a table, row click opens the record.
// No side panel here — a review is a full page, because confirming a reading and
// ruling on hundreds of blocks does not fit in a drawer.
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraBadge, SpecraButton, SpecraDataTable, SpecraSectionHeader, SpecraSwitchField } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenantId } from '../_shared/hooks/useTenantId';
import { usePricingSheetReviews } from './hooks';
import { PricingSheetIngestProgress } from './PricingSheetIngestProgress';
import { PricingSheetUploadDialog } from './PricingSheetUploadDialog';
import { useActiveIngests } from './useActiveIngests';
function formatWhen(ts) {
    if (!ts?.seconds) {
        return '—';
    }
    return new Date(Number(ts.seconds) * 1000).toLocaleDateString();
}
export function PricingSheetsTab() {
    const companyId = useTenantId();
    const navigate = useNavigate();
    // A LIST, not one slot: uploading a second sheet used to overwrite the first
    // and stop watching it.
    const { runs: activeRuns, track, untrack } = useActiveIngests(companyId);
    // Finished reviews are opt-in: the default question is "what is waiting on
    // me", and committed sheets would otherwise pile up above the open ones.
    const [includeApplied, setIncludeApplied] = useState(false);
    const [uploadOpen, setUploadOpen] = useState(false);
    const { data, isLoading } = usePricingSheetReviews(companyId, {
        includeApplied
    });
    const reviews = data ?? [];
    return /*#__PURE__*/ _jsxs("section", {
        children: [
            activeRuns.length > 0 ? /*#__PURE__*/ _jsx("div", {
                className: "mb-4 space-y-2",
                children: activeRuns.map((r)=>/*#__PURE__*/ _jsx(PricingSheetIngestProgress, {
                        filename: r.filename,
                        onSettled: ()=>untrack(r.runId),
                        runId: r.runId
                    }, r.runId))
            }) : null,
            /*#__PURE__*/ _jsx(PricingSheetUploadDialog, {
                onOpenChange: setUploadOpen,
                // Tracked, not navigated: several sheets can be in flight and each keeps
                // its own card.
                onStarted: (id, filename)=>track(id, filename),
                open: uploadOpen
            }),
            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                action: /*#__PURE__*/ _jsxs("div", {
                    className: "flex items-center gap-3",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraSwitchField, {
                            controlProps: {
                                checked: includeApplied,
                                onCheckedChange: setIncludeApplied
                            },
                            label: "Show committed"
                        }),
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            "data-testid": Testids.pricingSheetReview.uploadOpen,
                            onClick: ()=>setUploadOpen(true),
                            children: "Upload a sheet"
                        })
                    ]
                }),
                headingAs: "h2",
                title: `Pricing Sheets (${reviews.length})`
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "mt-3",
                children: /*#__PURE__*/ _jsx(SpecraDataTable, {
                    columns: [
                        {
                            id: 'title',
                            header: 'Sheet',
                            cell: (r)=>r.title || '(untitled sheet)'
                        },
                        {
                            id: 'state',
                            header: 'Status',
                            cell: (r)=>/*#__PURE__*/ _jsx(SpecraBadge, {
                                    variant: r.state === 'Applied' ? 'success' : r.blockingBlocks > 0 ? 'danger' : r.openBlocks > 0 ? 'warning' : 'neutral',
                                    children: r.state === 'Applied' ? 'Committed' : r.openBlocks > 0 ? `${r.openBlocks} awaiting a decision` : 'Ready to commit'
                                })
                        },
                        {
                            id: 'staged_by',
                            header: 'Staged by',
                            cell: (r)=>r.createdBy || '—'
                        },
                        {
                            align: 'right',
                            id: 'rates',
                            header: 'Rates',
                            cell: (r)=>r.rates.toLocaleString()
                        },
                        {
                            align: 'right',
                            id: 'blocks',
                            header: 'Blocks',
                            cell: (r)=>String(r.blocks)
                        },
                        {
                            align: 'right',
                            id: 'staged',
                            header: 'Staged',
                            cell: (r)=>formatWhen(r.createdAt)
                        }
                    ],
                    "data-testid": Testids.pricingSheetReview.list,
                    // What is missing, why, and one button.
                    emptyAction: /*#__PURE__*/ _jsx(SpecraButton, {
                        onClick: ()=>navigate('/registries?tab=vehicles'),
                        variant: "outline",
                        children: "Go to contract vehicles"
                    }),
                    emptyDescription: "A pricing sheet appears here once it has been uploaded against a contract vehicle and parsed. Nothing reaches the rate book until a sheet is reviewed and committed.",
                    emptyTitle: "No pricing sheets are waiting",
                    getRowActionLabel: (r)=>`Review ${r.rates.toLocaleString()} staged rates from ${r.title || 'this sheet'}`,
                    getRowId: (r)=>r.changeSetId,
                    isLoading: isLoading,
                    onRowClick: (r)=>navigate(`/registries/pricing-sheets/${r.changeSetId}`),
                    rowTestId: (r)=>`${Testids.pricingSheetReview.listRow}-${r.changeSetId}`,
                    rows: reviews
                })
            })
        ]
    });
}
